import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { MerchantProvider } from './contexts/MerchantContext';
import { MerchantDataProvider } from './contexts/MerchantDataContext';
import { OrderProvider } from './contexts/OrderContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { SUCCESS, ERROR, WARNING, INFO } from './theme/adminColors';

// Public pages (keep eager — they are entry points)
import LandingPage from './pages/LandingPage';
import MenuPage from './pages/MenuPage';
import TrackOrderPage from './pages/TrackOrderPage';

// Payment pages (keep eager — lightweight)
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailedPage from './pages/PaymentFailedPage';
import PaymentResultPage from './pages/PaymentResultPage';

// Auth pages (keep eager — first thing users see)
import LoginPage from './pages/admin/LoginPage';
import UserRegisterPage from './pages/admin/UserRegisterPage';
import EmailVerificationPage from './pages/admin/EmailVerificationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';

// Layout components
import AdminLayout from './components/layouts/AdminLayout';
import PublicLayout from './components/layouts/PublicLayout';
import PaymentLayout from './components/layouts/PaymentLayout';
import PaywallLayout from './components/layouts/PaywallLayout';
import SubscriptionAwareLayout from './components/layouts/SubscriptionAwareLayout';
import MerchantRequiredRoute from './components/auth/MerchantRequiredRoute';
import PaywallRoute from './components/auth/PaywallRoute';
import EmailVerifiedRoute from './components/auth/EmailVerifiedRoute';

// Skeleton fallbacks
import AdminLayoutSkeleton from './components/skeletons/AdminLayoutSkeleton';
import AdminDashboardSkeleton from './components/skeletons/AdminDashboardSkeleton';
import MenuManagementPageSkeleton from './components/skeletons/MenuManagementPageSkeleton';
import OrderManagementPageSkeleton from './components/skeletons/OrderManagementPageSkeleton';
import OrderHistoryPageSkeleton from './components/skeletons/OrderHistoryPageSkeleton';
import AnalyticsPageSkeleton from './components/skeletons/AnalyticsPageSkeleton';
import TableManagementPageSkeleton from './components/skeletons/TableManagementPageSkeleton';
import PaymentManagementPageSkeleton from './components/skeletons/PaymentManagementPageSkeleton';
import ProfileSettingsPageSkeleton from './components/skeletons/ProfileSettingsPageSkeleton';
import GeneralSettingsPageSkeleton from './components/skeletons/GeneralSettingsPageSkeleton';
import BrandingSettingsPageSkeleton from './components/skeletons/BrandingSettingsPageSkeleton';
import OrderSettingsPageSkeleton from './components/skeletons/OrderSettingsPageSkeleton';
import PaymentSettingsPageSkeleton from './components/skeletons/PaymentSettingsPageSkeleton';
import MembersSettingsPageSkeleton from './components/skeletons/MembersSettingsPageSkeleton';
import BillingPageSkeleton from './components/skeletons/BillingPageSkeleton';

// Lazy-loaded admin pages
const MerchantSetupPage = lazy(() => import('./pages/admin/MerchantSetupPage'));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const MenuManagementPage = lazy(() => import('./pages/admin/MenuManagementPage'));
const OrderManagementPage = lazy(() => import('./pages/admin/OrderManagementPage'));
const OrderHistoryPage = lazy(() => import('./pages/admin/OrderHistoryPage'));
const OrderDisplayPage = lazy(() => import('./pages/admin/OrderDisplayPage'));
const AnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'));
const TableManagementPage = lazy(() => import('./pages/admin/TableManagementPage'));
const PaymentManagementPage = lazy(() => import('./pages/admin/PaymentManagementPage'));
const SettingsPage = lazy(() => import('./pages/admin/SettingsPage'));
const ProfileSettingsPage = lazy(() => import('./pages/admin/settings/ProfileSettingsPage'));
const GeneralSettingsPage = lazy(() => import('./pages/admin/settings/GeneralSettingsPage'));
const BrandingSettingsPage = lazy(() => import('./pages/admin/settings/BrandingSettingsPage'));
const OrderSettingsPage = lazy(() => import('./pages/admin/settings/OrderSettingsPage'));
const PaymentSettingsPage = lazy(() => import('./pages/admin/settings/PaymentSettingsPage'));
const MembersSettingsPage = lazy(() => import('./pages/admin/settings/MembersSettingsPage'));

// Lazy-loaded subscription pages
const SubscriptionPlansPage = lazy(() => import('./pages/subscription/SubscriptionPlansPage'));
const TrialExpiredPage = lazy(() => import('./pages/subscription/TrialExpiredPage'));
const SubscriptionSuspendedPage = lazy(() => import('./pages/subscription/SubscriptionSuspendedPage'));
const BillingPage = lazy(() => import('./pages/admin/BillingPage'));

// Create a standard theme for admin pages
// Shared button metrics so every button renders at one consistent size
// regardless of the `size` prop passed at the call site.
const BUTTON_SIZE = {
  minHeight: 40,
  paddingTop: 8,
  paddingBottom: 8,
  paddingLeft: 18,
  paddingRight: 18,
  fontSize: '0.9rem',
};

const adminTheme = createTheme({
  palette: {
    primary: { main: '#667eea', dark: '#4f5bd5', contrastText: '#ffffff' },
    secondary: { main: '#dc004e' },
    // Shared semantic colors so every toast/alert uses one consistent palette.
    success: { main: SUCCESS.main, contrastText: '#ffffff' },
    error: { main: ERROR.main, contrastText: '#ffffff' },
    warning: { main: WARNING.main, contrastText: '#ffffff' },
    info: { main: INFO.main, contrastText: '#ffffff' },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          ...BUTTON_SIZE,
        },
        // Collapse the small/large size variants onto the shared metrics so
        // every button is the same height and padding across the admin.
        sizeSmall: { ...BUTTON_SIZE },
        sizeLarge: { ...BUTTON_SIZE },
        // Action buttons stay solid and flat (no elevation/gradient).
        contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    // Toasts default to the bottom-right so they stack in one place.
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      },
    },
    // One shared look for every toast Alert (filled variant): rounded, solid,
    // elevated, with white text/icons for legibility on the colored fill.
    // Inline (standard/outlined) Alert banners are left untouched.
    MuiAlert: {
      styleOverrides: {
        filled: {
          borderRadius: 16,
          fontWeight: 600,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
          color: '#ffffff',
          '& .MuiAlert-icon': { color: '#ffffff' },
          '& .MuiAlert-action': { color: '#ffffff' },
        },
      },
    },
  },
});

const App = () => {
  return (
    <MuiThemeProvider theme={adminTheme}>
      <CssBaseline />
      <AuthProvider>
        <MerchantProvider>
          <MerchantDataProvider>
            <FeatureFlagProvider>
              <SubscriptionProvider>
                <CurrencyProvider>
                  <NotificationProvider>
                    <OrderProvider>
                      <CartProvider>
                  <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/menu" element={<MenuPage />} />
                <Route path="/track-order" element={<TrackOrderPage />} />

                {/* Payment routes */}
                <Route path="/payment/success/:paymentId" element={<PaymentLayout><PaymentSuccessPage /></PaymentLayout>} />
                <Route path="/payment/failed/:paymentId" element={<PublicLayout><PaymentFailedPage /></PublicLayout>} />
                <Route path="/payment/result/:paymentId" element={<PublicLayout><PaymentResultPage /></PublicLayout>} />

                {/* Auth routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<UserRegisterPage />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/setup-merchant" element={
                  <EmailVerifiedRoute>
                    <Suspense fallback={<AdminLayoutSkeleton />}>
                      <MerchantSetupPage />
                    </Suspense>
                  </EmailVerifiedRoute>
                } />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

                {/* Admin routes - require merchant */}
                <Route path="/admin" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<AdminDashboardSkeleton />}>
                        <DashboardPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/menu" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<MenuManagementPageSkeleton />}>
                        <MenuManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/menu/categories" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<MenuManagementPageSkeleton />}>
                        <MenuManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/menu/items" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<MenuManagementPageSkeleton />}>
                        <MenuManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/menu/options" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<MenuManagementPageSkeleton />}>
                        <MenuManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<OrderManagementPageSkeleton />}>
                        <OrderManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders/board" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<OrderManagementPageSkeleton />}>
                        <OrderManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders/active" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<OrderManagementPageSkeleton />}>
                        <OrderManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders/history" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<OrderHistoryPageSkeleton />}>
                        <OrderHistoryPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders/display" element={
                  <MerchantRequiredRoute>
                    <Suspense fallback={<OrderManagementPageSkeleton />}>
                      <OrderDisplayPage />
                    </Suspense>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/orders/analytics" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<AnalyticsPageSkeleton />}>
                        <AnalyticsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/tables" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<TableManagementPageSkeleton />}>
                        <TableManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/payments" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<PaymentManagementPageSkeleton />}>
                        <PaymentManagementPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<AdminLayoutSkeleton />}>
                        <SettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/profile" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<ProfileSettingsPageSkeleton />}>
                        <ProfileSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/general" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<GeneralSettingsPageSkeleton />}>
                        <GeneralSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/branding" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<BrandingSettingsPageSkeleton />}>
                        <BrandingSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/orders" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<OrderSettingsPageSkeleton />}>
                        <OrderSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/payments" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<PaymentSettingsPageSkeleton />}>
                        <PaymentSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                <Route path="/admin/settings/members" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<MembersSettingsPageSkeleton />}>
                        <MembersSettingsPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                {/* Accept Invitation - Public route */}
                <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

                {/* Billing route */}
                <Route path="/admin/billing" element={
                  <MerchantRequiredRoute>
                    <AdminLayout>
                      <Suspense fallback={<BillingPageSkeleton />}>
                        <BillingPage />
                      </Suspense>
                    </AdminLayout>
                  </MerchantRequiredRoute>
                } />

                {/* Subscription routes */}
                <Route path="/admin/subscription/plans" element={
                  <MerchantRequiredRoute>
                    <SubscriptionAwareLayout>
                      <Suspense fallback={<AdminLayoutSkeleton />}>
                        <SubscriptionPlansPage />
                      </Suspense>
                    </SubscriptionAwareLayout>
                  </MerchantRequiredRoute>
                } />

                {/* Legacy redirects */}
                <Route path="/admin/subscription/manage" element={<Navigate to="/admin/billing" replace />} />
                <Route path="/admin/subscription" element={<Navigate to="/admin/subscription/plans" replace />} />

                <Route path="/trial-expired" element={
                  <PaywallRoute>
                    <PaywallLayout>
                      <Suspense fallback={<AdminLayoutSkeleton />}>
                        <TrialExpiredPage />
                      </Suspense>
                    </PaywallLayout>
                  </PaywallRoute>
                } />
                <Route path="/subscription-suspended" element={
                  <PaywallRoute>
                    <PaywallLayout>
                      <Suspense fallback={<AdminLayoutSkeleton />}>
                        <SubscriptionSuspendedPage />
                      </Suspense>
                    </PaywallLayout>
                  </PaywallRoute>
                } />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                      </CartProvider>
                    </OrderProvider>
                  </NotificationProvider>
                </CurrencyProvider>
              </SubscriptionProvider>
            </FeatureFlagProvider>
          </MerchantDataProvider>
        </MerchantProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

export default App;
